/** @format */
import { css } from 'styled-components';

import { DotColor, DotProps, DotSize } from './index';

/**
 * Base styles for the Dot component
 */
const baseStyles = () => css`
    display: inline-block;
    padding: 0;
    border: 0;
    border-radius: 50%;
    outline: none;

    :not([disabled]) {
        cursor: pointer;
    }

    &:focus,
    &:active {
        outline: var(--effect-border-lg) solid var(--effect-focus-ring);
    }
`;

/**
 * Get the styles for the given button size
 *
 * @param {DotProps}  props        the dot props
 * @param {DotSize}   props.size   the dot size
 * */
const sizeStyles = ({ size }: DotProps) => {
    switch (size) {
        case DotSize.SMALL:
            return css`
                width: 0.8rem;
                height: 0.8rem;
            `;
        case DotSize.MEDIUM:
        default:
            return css`
                width: 1.2rem;
                height: 1.2rem;
            `;
    }
};

/**
 * Get the styles for the disabled dot
 *
 * @param {DotProps}   props           the dot props
 * @param {boolean}    props.disabled  whether the dot is disabled
 * */
const disabledStyles = ({ disabled }: DotProps) => {
    if (!disabled) {
        return css``;
    }

    return css`
        &,
        &:hover,
        &:active,
        &:focus {
            background-color: var(--colors-surface-disabled);
        }
    `;
};

/**
 * Get the styles for the given button size
 *
 * @param {DotProps}  props        the dot props
 * @param {DotSize}   props.color   the dot color
 * */
const colorStyles = ({ dotColor }: DotProps) => {
    switch (dotColor) {
        case DotColor.INVERTED:
            return css`
                background-color: var(--colors-surface-tertiary);

                &:not([disabled]) {
                    &:hover,
                    &:active,
                    &:focus {
                        background-color: var(--colors-surface-accent-inverse);
                    }
                }

                &[aria-selected='true'] {
                    background-color: var(--colors-surface-inverse);
                }
            `;
        case DotColor.STANDARD:
        default:
            return css`
                background-color: var(--colors-surface-inverse);

                &:not([disabled]) {
                    &:hover,
                    &:active,
                    &:focus {
                        background-color: var(--colors-surface-accent-inverse);
                    }
                }

                &[aria-selected='true'] {
                    background-color: var(--colors-surface-secondary);
                }
            `;
    }
};

/**
 * The Dot component styles
 *
 * @param {DotProps} props The dot properties object
 */
export const styles = (props: DotProps) => css`
    ${baseStyles()}
    ${sizeStyles(props)}
    ${disabledStyles(props)}
    ${colorStyles(props)}
`;
